<script setup lang="ts">
import { toSnakeCase } from '~/helpers/utils'
import { TransformedArticle } from '~/components/cms/BlogArticlePanel/blogArticleHelper'

type ArticleProps = {
  variant?: 'featured-article' | 'related-article' | 'article-list'
  item: TransformedArticle
}
const props = defineProps<ArticleProps>()

// @ts-ignore
const { sys, fields } = props.item.picture
const mobilePicture = fields?.mobilePicture
const desktopPicture = mobilePicture || fields?.picture /* Use MobilePicture when available for better crop on Card */
const pictureFields = { sys, fields: { picture: desktopPicture, mobilePicture } }
const variantClass = props.variant === 'featured-article' ? 'article-card--featured' : ''
const picturePreset = props.variant === 'featured-article' ? 'featured-card' : 'article-card'
</script>

<template>
  <article class="article-card" :class="variantClass">
    <Media class="article-card__image" v-bind="pictureFields" :preset="picturePreset" loading="lazy" />
    <div class="article-card__content">
      <div v-if="variant === 'featured-article'" class="article-card__pre-heading">
        {{ $t('feature_article_card.pre_heading') }}
      </div>
      <Anchor variant="tertiary" class="article-card__heading" :href="item.url">
        <h4 class="heading-3">{{ item.title }}</h4>
      </Anchor>
      <div class="article-card__tags">
        <template v-for="(tag, index) in item.tags" :key="tag">
          <span v-if="index > 0" :key="'|' + tag"> | </span>
          <span class="article-card__tag-item">{{ $t(`article_tags.${toSnakeCase(tag)}`) }}</span>
        </template>
      </div>
      <p class="article-card__description">
        {{ item.description }}
      </p>
      <Anchor variant="tertiary" class="article-card__action" :href="item.url">
        {{ $t('feature_article_card.read_article') }}
      </Anchor>
    </div>
  </article>
</template>

<style lang="postcss">
.article-card {
  --hover-scale: 1.05;

  position: relative;
  display: flex;
  flex-direction: column;

  @media (--desktop) {
    max-width: 19.3125rem;
  }
  .article-card__image {
    overflow: hidden;
    aspect-ratio: var(--aspect-ratio-product);
    height: auto;

    .picture__img {
      aspect-ratio: var(--aspect-ratio-product);
      object-fit: cover;
      transition: var(--transition-ease-out-1);
    }
  }

  .article-card__content {
    padding: var(--space-50) var(--space-50) var(--space-70) var(--space-50);
    text-align: left;

    .article-card__heading {
      text-decoration: none;

      h4 {
        line-height: 120%;
        font-size: 1rem;
        margin-bottom: 0;
      }
    }
  }

  .article-card__description {
    margin-top: var(--space-50);
    margin-bottom: var(--space-60);
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  .article-card__tags {
    margin-top: var(--space-20);
    font-size: var(--text-xsmall);
    text-transform: uppercase;
  }

  .article-card__action {
    text-transform: uppercase;
    display: unset;

    &::before {
      position: absolute;
      inset: 0 0 0 0;
      z-index: 1;
      content: '';
    }
  }

  /* Zoom image animation on Card hover */
  &:has(.article-card__action:hover) {
    .article-card__heading {
      color: var(--text-link-hover);
    }
    .picture__img,
    .video__video {
      scale: var(--hover-scale);
    }
  }
}

.article-card.article-card--featured {
  max-width: unset;

  @media (--tablet) {
    display: grid;
    grid-template-columns: 4fr 2fr;
  }

  .article-card__image {
    overflow: hidden;
    aspect-ratio: unset;

    .picture__img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      aspect-ratio: var(--aspect-ratio-5-4);

      @media (--tablet) {
        aspect-ratio: auto;
        max-height: 22.5rem;
      }
    }
  }

  .article-card__content {
    margin: var(--space-50) var(--space-50) 0;

    @media (--tablet) {
      margin: auto 0;
      max-width: 26rem;
    }

    @media (--desktop) {
      margin: auto var(--space-70);
    }
  }

  .article-card__pre-heading {
    font-weight: bold;
    color: var(--text-secondary);
    font-size: var(--text-small);
    text-transform: uppercase;
  }

  .article-card__heading {
    text-decoration: none;

    h4 {
      line-height: 120%;
      font-size: var(--text-xlarge);
      margin-bottom: 0;
      margin-top: var(--space-50);
    }
  }

  .article-card__description {
    margin-top: var(--space-50);
    margin-bottom: 0;
  }
}
</style>
